import { useEffect, useState } from 'react';

/**
 * `true` quando una fonte non ha ancora risposto dopo piu' tempo di quanto sia ragionevole
 * chiamarlo caricamento.
 *
 * 🔴 Perche' esiste — e la motivazione e' stata CORRETTA il 2026-09-06 prima del rilascio,
 * perche' la prima era falsa. Diceva «spinner infinito»: misurato campionando `animate-spin`
 * a 5/15/25/35 s (or45), gli spinner **si fermano da soli** e dicono l'errore vero — dopo
 * **15-25 secondi**. Il guasto quindi non e' un'attesa infinita: e' un'**attesa MUTA lunga
 * un quarto di minuto** su una WebView di telefono, in cui lo schermo non dice niente.
 *
 * 📌 *«Infinito» e' un'affermazione su tutti gli istanti futuri: non si puo' dedurre da
 * un'osservazione singola.* La prima misura aveva campionato a 4-5 secondi, e un istante
 * solo non distingue «infinito» da «lento» — la stessa faglia del vuoto affermato: un
 * criterio che mappa due stati diversi sullo stesso valore.
 *
 * ⛔ Ne segue un vincolo su chi usa questo hook: la scadenza deve poter arrivare PRIMA del
 * messaggio d'errore vero, quindi **il testo che mostra non puo' essere piu' povero di
 * quello che sostituisce**. Anticipare un messaggio e' un miglioramento; impoverirlo no.
 * (Per questo `LeaderboardPanel` tiene la propria `load_error` e non un avviso generico.)
 *
 * ⚠️ Non e' un timeout della richiesta: la chiamata prosegue e, se arriva, vince. Questo
 * cambia solo cosa DICE lo schermo mentre non e' arrivata — da «sto caricando» (che dopo un
 * po' e' una promessa che non possiamo mantenere) a «non lo so».
 *
 * 🔴 **`pending` va derivato da «non ha MAI risposto» (`data === undefined`), NON da
 * `isLoading`** — e questa riga e' costata una correzione a caldo il 2026-09-06, prima ancora
 * di arrivare in produzione. Con `pollingInterval` acceso e un backend a 503, `isLoading`
 * LAMPEGGIA: e' vero mentre la richiesta e' in volo e falso appena viene respinta, un giro
 * dopo l'altro. Un timer armato su quel booleano si azzera a ogni lampeggio e **non scade
 * mai**: la difesa contro l'attesa muta sarebbe stata essa stessa muta per sempre.
 * `data === undefined` invece non lampeggia — RTK Query lo popola solo con una risposta
 * riuscita e non lo cancella su un errore successivo — quindi il tempo si misura da quando
 * la schermata e' aperta, che e' il tempo che l'utente sta effettivamente aspettando.
 * 📌 *Lo stesso errore di premessa del vuoto affermato: dedurre dal client cio' che solo il
 * server puo' dire.*
 */
/**
 * Le DUE soglie, e sono due perche' dicono cose diverse.
 *
 * 🔴 La seconda e' nata da un guasto visto da una giocatrice vera il 2026-09-06: la scheda
 * Eventi ferma su «Ci sta mettendo piu' del previsto» per MINUTI. `slow` era stato definito
 * «stato di transizione» — ma senza un limite, una transizione che non transisce e' uno stato
 * fermo, e ripetere «ancora un attimo» per minuti e' esattamente la promessa che non possiamo
 * mantenere.
 * ⛔ Oltre `NO_ANSWER_MS` la fonte si dichiara NON RISPONDENTE. Non e' una diagnosi sul
 * server: e' cio' che sappiamo — non ha risposto, e non abbiamo altro da dire.
 */
export const STALL_MS = 12_000;
export const NO_ANSWER_MS = 45_000;

export function useStalled(pending: boolean, ms = STALL_MS): boolean {
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    if (!pending) {
      setStalled(false);
      return;
    }
    const id = window.setTimeout(() => setStalled(true), ms);
    return () => window.clearTimeout(id);
  }, [pending, ms]);
  return pending && stalled;
}

/**
 * `true` da quando una fonte ha fallito **almeno una volta** e finche' non risponde.
 *
 * 🔴 Serve perche' `isError` di RTK Query LAMPEGGIA come `isLoading`, e in senso opposto:
 * appena riparte il giro di `pollingInterval` torna falso. Senza questa memoria, una scheda
 * su un backend a 503 alternerebbe l'errore vero («Errore nel caricamento dei tornei») e
 * l'attesa lenta («ci sta mettendo troppo») a ogni giro — **cedendo il posto al contrario**
 * di quanto deve: e' il messaggio piu' informativo a dover restare.
 *
 * ⚠️ Si azzera quando la fonte risponde (`answered`), o un successo dopo un fallimento
 * resterebbe marchiato per sempre e un elenco legittimamente vuoto verrebbe letto come un
 * errore.
 *
 * 📌 Stessa lezione dell'hook qui sopra: **una decisione si arma su cio' che non lampeggia.**
 */
export function useEverFailed(failed: boolean, answered: boolean): boolean {
  const [ever, setEver] = useState(false);
  useEffect(() => {
    if (answered) setEver(false);
    else if (failed) setEver(true);
  }, [failed, answered]);
  return ever && !answered;
}
